import React, { useEffect, useState, useContext } from 'react'
import { faChevronDown } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import countries from 'i18n-iso-countries'
import { getCountries, getCountryCallingCode } from 'libphonenumber-js'
import CustomDropdown from 'Components/CustomDropdown/CustomDropdown'
import PropTypes from 'prop-types'
import { t } from 'i18next'
import { getValueFromPath } from 'Utils/Helpers/getValueFromPath'
import { DEFAULT_PHONE_PREFIX } from 'Utils/constants'
import { VAContext } from 'Context/VAContext'
import './PhoneNumberField.css'

countries.registerLocale(require('i18n-iso-countries/langs/es.json'))

export const PhoneNumberField = ({
  formik,
  fieldName,
  fieldToCheck,
  value: externalValue,
  onChange,
  optional,
}) => {
  const { clientSelected } = useContext(VAContext)
  const isEditing = clientSelected?.edit
  const isUsingFormik = !!formik && fieldName

  const [phonePrefixesByCountry, setPhonePrefixesByCountry] = useState([])
  const [selectedPrefix, setSelectedPrefix] = useState(
    !isEditing ? DEFAULT_PHONE_PREFIX : ''
  )
  const [localValue, setLocalValue] = useState({
    Prefix: DEFAULT_PHONE_PREFIX,
    number: '',
  })

  const value = isUsingFormik
    ? getValueFromPath(formik.values, fieldName) || {}
    : externalValue || localValue

  useEffect(() => {
    const countryList = getCountries().map(countryCode => {
      const countryName = countries.getName(countryCode, 'es')
      const callingCode = `+${getCountryCallingCode(countryCode)}`
      return {
        countryCode,
        label: `${callingCode} ${countryName || countryCode}`,
        prefix: callingCode,
        name: countryName || countryCode,
      }
    })

    setPhonePrefixesByCountry(countryList)

    const initialPrefix = isUsingFormik
      ? getValueFromPath(formik.values, fieldName)?.Prefix
      : externalValue?.Prefix

    if (initialPrefix) {
      const selectedCountry = countryList.find(
        country => country.prefix === initialPrefix
      )
      if (selectedCountry) {
        setSelectedPrefix(selectedCountry.prefix)

        if (isUsingFormik) {
          formik.setFieldValue(
            `${fieldName}.countryCode`,
            selectedCountry.countryCode
          )
        } else {
          const updated = {
            ...value,
            countryCode: selectedCountry.countryCode,
          }
          setLocalValue(updated)
          onChange?.(updated)
        }
      }
    }
  }, [])

  const handleInputChange = e => {
    const onlyNums = e.target.value.replace(/\D/g, '').slice(0, 8)

    if (isUsingFormik) {
      formik.setFieldValue(`${fieldName}.number`, onlyNums)
    } else {
      const updated = { ...value, number: onlyNums }
      setLocalValue(updated)
      onChange?.(updated)
    }
  }

  const handleSelectCountry = item => {
    setSelectedPrefix(item.prefix)

    if (isUsingFormik) {
      formik.setFieldValue(`${fieldName}.Prefix`, item.prefix)
      formik.setFieldValue(`${fieldName}.countryCode`, item.countryCode)
    } else {
      const updated = {
        number: typeof value === 'string' ? value : value?.number || '',
        Prefix: item.prefix,
        countryCode: item.countryCode,
      }
      setLocalValue(updated)
      onChange?.(updated)
    }
  }

  const hasBeenTouched =
    isUsingFormik && Object.hasOwn(formik.touched, fieldToCheck)
  const hasError = isUsingFormik && Object.hasOwn(formik.errors, fieldToCheck)
  const hasNumberError = hasError && formik.errors[fieldToCheck]?.[0]?.number

  const inputClassName =
    hasBeenTouched && hasNumberError ? 'form-control error' : 'form-control'

  return (
    <div className="phone-number-wrapper">
      <div className="telephone-prefix" data-testid="dropdown-phone-prefix">
        <CustomDropdown
          showSearch
          placeholder={t('CREATE_CLIENT_T.PREFIX')}
          selectedItem={selectedPrefix}
          items={phonePrefixesByCountry}
          onSelect={handleSelectCountry}
          searchPlaceholder={t('CREATE_CLIENT_T.SEARCH_FOR_COUNTRY')}
          renderSelected={item => item.countryCode}
          compareItems={(a, b) => a.prefix === b}
          wrapperStyle={{
            width: '100px',
            position: 'initial',
            borderRight: 'none',
            borderRadius: '5px 0 0 5px',
          }}
        />
        <FontAwesomeIcon
          icon={faChevronDown}
          className="down-icon-phone-field"
        />
      </div>
      <div className="telephone-input-wrapper prefixed-input">
        <span className="input-prefix">{selectedPrefix}</span>
        <input
          type="text"
          autoComplete="off"
          placeholder={optional ? '0000-0000' : '0000-0000*'}
          data-testid="input-phone-number"
          onBlur={isUsingFormik ? formik.handleBlur : undefined}
          onChange={handleInputChange}
          name={isUsingFormik ? `${fieldName}.number` : undefined}
          className={`${inputClassName} input-right-rounded`}
          value={typeof value === 'string' ? value : value?.number || ''}
        />
      </div>
    </div>
  )
}

PhoneNumberField.propTypes = {
  formik: PropTypes.shape({
    setFieldValue: PropTypes.func,
    handleBlur: PropTypes.func,
    values: PropTypes.object,
    errors: PropTypes.object,
    touched: PropTypes.object,
  }),
  fieldName: PropTypes.string,
  fieldToCheck: PropTypes.string,
  value: PropTypes.shape({
    Prefix: PropTypes.string,
    number: PropTypes.string,
  }),
  onChange: PropTypes.func,
}

export default PhoneNumberField
